import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isFloat, timeToFloat, removeCommas, convertAutomatically, sanitizeInputValue } from './utilities';

class FloatInput extends Component {
  constructor(props) {
    super(props);
    this.state = {
      displayValue: timeToFloat(props.value),
      internalValue: props.value,
      isTyping: false,
    };

    this.debounceTimeout = null;
    this.isComposing = false;
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.value !== prevState.internalValue && !prevState.isTyping) {
      return {
        displayValue: timeToFloat(nextProps.value),
        internalValue: nextProps.value,
      };
    }

    return null;
  }

  componentWillUnmount() {
    clearTimeout(this.debounceTimeout);
  }

  handleCompositionStart = () => {
    // #2250: IME(全角)入力を許容する。変換中はそのまま表示し、確定(compositionend)で
    // 半角へ自動変換 + 不許可文字を削除する。警告トーストは出さない。
    this.isComposing = true;
  };

  handleCompositionEnd = (event) => {
    this.isComposing = false;
    this.handleInputChange(event);
  };

  handleInputChange = (event) => {
    // While the IME is composing, show the raw value without converting or
    // validating. Writing a converted/validated value back into the controlled
    // input mid-composition makes the IME re-insert its buffer and duplicates
    // characters. Conversion/validation runs once on compositionend instead.
    if (this.isComposing) {
      clearTimeout(this.debounceTimeout);
      this.setState({ displayValue: event.target.value, isTyping: true });

      this.debounceTimeout = setTimeout(() => {
        this.setState({ isTyping: false });
      }, 500);

      return;
    }

    // #2250: 全角→半角へ変換し、数値欄の許可文字 (数字/演算子/小数点/カンマ) 以外を
    // 削除する。全角や不許可記号を入力しても半角の許容文字だけが残る。
    let value = convertAutomatically(removeCommas(event.target.value), this.props.inputType || '');
    value = sanitizeInputValue(value);

    // サニタイズ後も数値(float)にならない中途半端な値(例: "1.2.3")は表示だけ更新し、
    // 親へは伝播しない。従来のように直前値へ全戻しはせず、削除後の値をそのまま見せる
    // ことで「不許可文字が消えた」ことがユーザーに分かるようにする。
    // isFloat は ""/"-"/"1." 等の入力途中の有効値も許容するため通常入力は反映される。
    if (!isFloat(value)) {
      clearTimeout(this.debounceTimeout);
      this.setState({ displayValue: value, isTyping: false });
      return;
    }

    clearTimeout(this.debounceTimeout);
    // 親 (automaticCalculatePayroll) も removeCommas + convertAutomatically を行うが、
    // 全角記号など sanitize 済みの結果を確実に渡すため、変換・削除後の値で
    // event.target.value を上書きしてから渡す。
    event.target.value = value;
    this.setState({ displayValue: value, internalValue: value, isTyping: true }, () => {
      this.props.onChange(event);
    });

    this.debounceTimeout = setTimeout(() => {
      this.setState({ isTyping: false });
    }, 500);
  };

  render() {
    // onImeWarning / inputType は内部用なので DOM input へは渡さない。
    const { value, onImeWarning, inputType, ...otherProps } = this.props;

    return (
      <input
        {...otherProps}
        onChange={this.handleInputChange}
        onCompositionEnd={this.handleCompositionEnd}
        onCompositionStart={this.handleCompositionStart}
        value={this.state.displayValue}
      />
    );
  }
}

FloatInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  inputType: PropTypes.string,
  onImeWarning: PropTypes.func,
};

export default FloatInput;
